import { useEffect, useMemo, useRef, useState } from "react";
import {
	User,
	Mail,
	Phone,
	MapPin,
	Lock,
	Eye,
	Pencil,
	Clock,
	Calendar,
	FileText,
	AlertTriangle,
	Trash2,
	X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChangeUserPassword } from "../hooks/control";
import { logout } from "../core/auth/auth";
import { useFetch } from "../hooks/useFetch";
import type { ApiAddress } from "../core/types/tsx/pages/UserProfile.types";
import type {
	UserPersonalInfo,
	UserOrder,
} from "../core/types/tsx/components/UserDetails.types";
import {
	searchAdministrativeNominatim,
	type AdministrativeSuggestion,
} from "./user-details/nominatim";
import { AlertModal, type AlertVariant } from "./modals/AlertModal";
import { ConfirmModal, type ConfirmVariant } from "./modals/ConfirmModal";
import { ReportReasonModal } from "./modals/ReportReasonModal";
import { AddressCardsBoard } from "./common/AddressCardsBoard";
import { ClientFavorites } from "./profile/ClientFavorites";
import {
	composePhoneWithCountryCode,
	getPhoneCountryCodeOptions,
	getPhoneValidationError,
	splitPhoneInputValue,
	splitPhoneWithCountryCode,
} from "../core/utils/phone";

type AlertConfig = {
	title: string;
	message: string;
	variant?: AlertVariant;
	confirmText?: string;
};

type ConfirmConfig = {
	title: string;
	message: string;
	variant?: ConfirmVariant;
	confirmLabel?: string;
	cancelLabel?: string;
};

type UserAddressRow = {
	id: number;
	state: string;
	city: string;
	street: string;
	reference: string;
	isActive: boolean;
};

type PersonalInfoSavePayload = UserPersonalInfo & {
	firstName: string;
	lastName: string;
};

const splitNameParts = (fullName: string): { firstName: string; lastName: string } => {
	const tokens = fullName.trim().split(/\s+/).filter(Boolean);

	if (tokens.length === 0) return { firstName: "", lastName: "" };
	if (tokens.length === 1) return { firstName: tokens[0], lastName: "" };
	if (tokens.length === 2) {
		return { firstName: tokens[0], lastName: tokens[1] };
	}

	return {
		firstName: tokens.slice(0, -2).join(" "),
		lastName: tokens.slice(-2).join(" "),
	};
};

// ----------------------------------------------------------------------
// 1. SUB-COMPONENTE: DATOS PERSONALES
// ----------------------------------------------------------------------

const personalFallback: UserPersonalInfo = {
	id: 0,
	name: "-",
	email: "-",
	phone: "-",
	municipality: "-",
	address: "-",
};

const PersonalData = ({
	profile,
	onSavePersonalInfo,
	isSaving = false,
	onNotify,
}: {
	profile: UserPersonalInfo;
	onSavePersonalInfo?: (payload: PersonalInfoSavePayload) => Promise<void>;
	isSaving?: boolean;
	onNotify?: (payload: AlertConfig) => void;
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [form, setForm] = useState<UserPersonalInfo>(profile);
	const [nameForm, setNameForm] = useState<{ firstName: string; lastName: string }>(
		() => splitNameParts(profile.name),
	);
	const phoneParts = useMemo(() => splitPhoneWithCountryCode(form.phone), [form.phone]);
	const profileNameParts = useMemo(() => splitNameParts(profile.name), [profile.name]);
	const phoneCountryCodeOptions = useMemo(
		() => getPhoneCountryCodeOptions(phoneParts.countryCode),
		[phoneParts.countryCode],
	);

	useEffect(() => {
		if (isEditing) return;
		setForm(profile);
		setNameForm(splitNameParts(profile.name));
	}, [profile, isEditing]);

	const handleStartEdit = () => {
		setForm(profile);
		setNameForm(splitNameParts(profile.name));
		setIsEditing(true);
	};

	const handleCancel = () => {
		setForm(profile);
		setNameForm(splitNameParts(profile.name));
		setIsEditing(false);
	};

	const handleSave = async () => {
		const normalizedFirstName = nameForm.firstName.trim();
		const normalizedLastName = nameForm.lastName.trim();
		if (normalizedFirstName.length < 2) {
			onNotify?.({
				title: "Nombre inválido",
				message: "El nombre debe tener al menos 2 caracteres.",
				variant: "warning",
			});
			return;
		}

		if (normalizedLastName.length < 2) {
			onNotify?.({
				title: "Apellido inválido",
				message: "El apellido debe tener al menos 2 caracteres.",
				variant: "warning",
			});
			return;
		}

		const normalizedEmail = form.email.trim();
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
			onNotify?.({
				title: "Correo inválido",
				message: "Ingresa un correo electrónico válido.",
				variant: "warning",
			});
			return;
		}

		const normalizedPhone = composePhoneWithCountryCode(
			phoneParts.countryCode,
			phoneParts.localNumber,
		);
		const phoneError = getPhoneValidationError(normalizedPhone, {
			required: true,
		});
		if (phoneError) {
			onNotify?.({
				title: "Teléfono inválido",
				message: phoneError,
				variant: "warning",
			});
			return;
		}

		if (!onSavePersonalInfo) {
			onNotify?.({
				title: "Editar",
				message: "La edición de perfil estará disponible próximamente.",
				variant: "info",
			});
			return;
		}
		await onSavePersonalInfo({
			...form,
			name: `${normalizedFirstName} ${normalizedLastName}`.trim(),
			firstName: normalizedFirstName,
			lastName: normalizedLastName,
			phone: normalizedPhone,
		});
		setIsEditing(false);
	};

	const inputReadOnlyClass =
		"w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none";
	const inputEditableClass =
		"w-full border border-slate-300 bg-white rounded-xl px-4 py-3 text-sm text-slate-700 outline-none focus:border-primary transition-all";

	return (
		<div className='animate-in fade-in slide-in-from-bottom-4 duration-500'>
			<div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
				<div>
					<h2 className='text-xl sm:text-2xl font-semibold text-gray-800'>
						Información Personal
					</h2>
					<p className='text-sm text-gray-500'>
						Actualiza tu información personal
					</p>
				</div>

				<div className='flex items-center gap-2'>
					{!isEditing ? (
						<button
							type='button'
							onClick={handleStartEdit}
							className='cursor-pointer inline-flex h-10 items-center justify-center gap-2 self-start rounded-xl bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							<Pencil className='h-4 w-4' />
							Editar
						</button>
					) : (
						<>
							<button
								type='button'
								onClick={handleCancel}
								className='cursor-pointer h-10 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50'
							>
								Cancelar
							</button>
							<button
								type='button'
								disabled={isSaving}
								onClick={() => void handleSave()}
								className='cursor-pointer h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60'
							>
								{isSaving ? "Guardando..." : "Guardar"}
							</button>
						</>
					)}
				</div>
			</div>

			<div className='space-y-6'>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					{/* Nombre */}
					<div>
						<label
							htmlFor='user-first-name'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<User size={16} /> Nombre
						</label>
						<input
							id='user-first-name'
							type='text'
							readOnly={!isEditing}
							value={
								isEditing
									? nameForm.firstName
									: profileNameParts.firstName
							}
							onChange={(e) => {
								const sanitized = e.target.value.replace(
									/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s'-]/g,
									"",
								);
								setNameForm((prev) => ({
									...prev,
									firstName: sanitized,
								}));
							}}
							className={
								isEditing ? inputEditableClass : inputReadOnlyClass
							}
						/>
					</div>

					{/* Apellido */}
					<div>
						<label
							htmlFor='user-last-name'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<User size={16} /> Apellido
						</label>
						<input
							id='user-last-name'
							type='text'
							readOnly={!isEditing}
							value={
								isEditing ? nameForm.lastName : profileNameParts.lastName
							}
							onChange={(e) => {
								const sanitized = e.target.value.replace(
									/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s'-]/g,
									"",
								);
								setNameForm((prev) => ({ ...prev, lastName: sanitized }));
							}}
							className={
								isEditing ? inputEditableClass : inputReadOnlyClass
							}
						/>
					</div>

					{/* Correo Electrónico */}
					<div>
						<label
							htmlFor='user-email'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<Mail size={16} /> Correo Electrónico
						</label>
						<input
							id='user-email'
							type='email'
							readOnly={!isEditing}
							value={isEditing ? form.email : profile.email}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, email: e.target.value }))
							}
							className={
								isEditing ? inputEditableClass : inputReadOnlyClass
							}
						/>
					</div>

					{/* Teléfono */}
					<div>
						<label
							htmlFor='user-phone'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<Phone size={16} /> Teléfono
						</label>
						{isEditing ? (
							<div className='grid grid-cols-[140px_1fr] gap-2 sm:grid-cols-[180px_1fr]'>
								<select
									aria-label='Código de área'
									value={phoneParts.countryCode}
									onChange={(e) =>
										setForm((prev) => ({
											...prev,
											phone: composePhoneWithCountryCode(
												e.target.value,
												phoneParts.localNumber,
											),
										}))
									}
									className='w-full border border-slate-300 bg-white rounded-xl px-3 py-3 text-sm text-slate-700 outline-none focus:border-primary transition-all'
								>
									{phoneCountryCodeOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
								<input
									id='user-phone'
									type='tel'
									value={phoneParts.localNumber}
									onChange={(e) => {
										const nextPhoneParts = splitPhoneInputValue(
											e.target.value,
											phoneParts.countryCode,
											{
												inferCallingCodeFromPlainDigits: false,
												codeOnlyMeansEmptyLocal: false,
											},
										);
										setForm((prev) => ({
											...prev,
											phone: composePhoneWithCountryCode(
												nextPhoneParts.countryCode,
												nextPhoneParts.localNumber,
											),
										}));
									}}
									inputMode='tel'
									pattern='^\+?[0-9()\s-]{7,20}$'
									className={inputEditableClass}
								/>
							</div>
						) : (
							<input
								id='user-phone'
								type='tel'
								readOnly
								value={profile.phone}
								inputMode='tel'
								pattern='^\+?[0-9()\s-]{7,20}$'
								className={inputReadOnlyClass}
							/>
						)}
					</div>

					{/* Fecha de Nacimiento */}
					<div>
						<label
							htmlFor='user-dob'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<Calendar size={16} /> Fecha de Nacimiento (opcional)
						</label>
						<input
							id='user-dob'
							type={isEditing ? "date" : "text"}
							readOnly={!isEditing}
							value={
								isEditing
									? (form.dateOfBirth ?? "")
									: (profile.dateOfBirth
										? (() => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(profile.dateOfBirth!); return m ? `${m[3]}/${m[2]}/${m[1]}` : profile.dateOfBirth!; })()
										: "-")
							}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, dateOfBirth: e.target.value || null }))
							}
							className={isEditing ? inputEditableClass : inputReadOnlyClass}
						/>
					</div>

					{/* Género */}
					<div>
						<label
							htmlFor='user-gender'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<User size={16} /> Género (opcional)
						</label>
						{isEditing ? (
							<select
								id='user-gender'
								value={form.gender ?? ""}
								onChange={(e) =>
									setForm((prev) => ({ ...prev, gender: e.target.value || null }))
								}
								className={inputEditableClass}
							>
								<option value=''>Sin especificar</option>
								<option value='Masculino'>Masculino</option>
								<option value='Femenino'>Femenino</option>
								<option value='Otro'>Prefiero no decirlo</option>
							</select>
						) : (
							<input
								id='user-gender'
								type='text'
								readOnly
								value={profile.gender ?? "-"}
								className={inputReadOnlyClass}
							/>
						)}
					</div>

					{/* Ubicación */}
					{/* <div>
						<label
							htmlFor='user-address'
							className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'
						>
							<MapPin size={16} /> Ubicación
						</label>
						<NominatimAddressInput
							value={isEditing ? form.address : profile.address}
							onChange={(v) => setForm((prev) => ({ ...prev, address: v }))}
							onPickLocation={(coords) => {
								setPickedLocation(coords);
								setMapCenter({ lat: coords.lat, lng: coords.lng });
								setMapZoom(16);
							}}
							readOnly={!isEditing}
							placeholder='Ej: Caracas, Chacao'
							inputClassName={
								isEditing ? inputEditableClass : inputReadOnlyClass
							}
						/>
					</div> */}
				</div>
			</div>
		</div>
	);
};

// ----------------------------------------------------------------------
// 2. SUB-COMPONENTE: MIS PEDIDOS
// ----------------------------------------------------------------------
const statusColors: Record<string, string> = {
	// Order statuses
	Dialogando: "bg-[#ECD4FB] text-[#8313C8] border border-[#8313C8]",
	"En Espera": "bg-[#DBEAFE] text-[#1447E6] border border-[#1447E6]",
	"En Progreso": "bg-[#DCFCE7] text-[#008235] border border-[#008235]",
	"Por pagar": "bg-[#DCFCE7] text-[#008235] border border-[#008235]",
	Completado: "bg-[#DCFCE7] text-[#008235] border border-[#008235]",
	Cancelado: "bg-[#FFD4CE] text-[#E93A1F] border border-[#E93A1F]",
	Soporte: "bg-[#DBEAFE] text-[#1447E6] border border-[#1447E6]",
	Garantía: "bg-[#FEFCE8] text-[#894C00] border border-[#894C00]",
	Pagado: "bg-[#DCFCE7] text-[#008235] border border-[#008235]",
	"Pago Rechazado": "bg-[#FFD4CE] text-[#E93A1F] border border-[#E93A1F]",
	// Request statuses
	"Solicitud Pendiente": "bg-[#FEFCE8] text-[#894C00] border border-[#894C00]",
	"Cotización Recibida": "bg-indigo-50 text-indigo-700 border-indigo-200",
	"Solicitud Aceptada": "bg-[#DCFCE7] text-[#008235] border border-[#008235]",
	"Solicitud Rechazada": "bg-[#FFD4CE] text-[#E93A1F] border border-[#E93A1F]",
	"Solicitud Cancelada": "bg-red-50 text-red-700 border-red-200",
};

const statusLabelByValue: Record<string, string> = {
	"Solicitud Pendiente": "Pendiente",
	"Solicitud Aceptada": "Aceptado",
	"Solicitud Rechazada": "Rechazada",
	"Pago Rechazado": "Pago rechazado",
};

type OrderFilter = "all" | "orders" | "requests";

const normalizeOrderStatusFilterValue = (value?: string | null) =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[\s_]+/g, " ")
		.trim();

const MyOrders = ({
	orders,
	onNotify,
	//	onRequestConfirm,
}: {
	orders: UserOrder[];
	onNotify?: (payload: AlertConfig) => void;
	//	onRequestConfirm?: (payload: ConfirmConfig) => Promise<boolean>;
}) => {
	const navigate = useNavigate();
	const { execute: executePatchOrderStatus } = useFetch<
		unknown,
		{ status: string; reason: string }
	>({
		endpoint: "/orders/1/status",
		method: "PATCH",
		auto: false,
	});
	const [ordersState, setOrdersState] = useState<UserOrder[]>(orders);
	const [orderFilter, setOrderFilter] = useState<OrderFilter>("all");
	const [statusFilter, setStatusFilter] = useState("");
	const [reportingOrderId, setReportingOrderId] = useState<UserOrder["id"] | null>(
		null,
	);
	const [reportModalOrder, setReportModalOrder] = useState<UserOrder | null>(null);
	const [reportReason, setReportReason] = useState("");
	const [reportReasonError, setReportReasonError] = useState("");

	const handleOpenOrderDetails = (order: UserOrder) => {
		const detailsId = Number(order.id);
		if (!Number.isFinite(detailsId) || detailsId <= 0) return;
		navigate(`/info/pedido/${detailsId}`, {
			state: { orderId: detailsId },
		});
	};

	const handleOpenPaymentRegistration = (order: UserOrder) => {
		const detailsId = Number(order.id);
		if (!Number.isFinite(detailsId) || detailsId <= 0) return;
		navigate("/registro/servicio", {
			state: {
				orderId: detailsId,
				resetPaymentForm: true,
				paymentFlowToken: Date.now(),
			},
		});
	};

	const resolveOrderDetailsId = (order: UserOrder): number | null => {
		const direct = Number(order.id);
		if (Number.isFinite(direct) && direct > 0) return direct;
		const fromCode = Number(order.numeroPedido ?? 0);
		if (Number.isFinite(fromCode) && fromCode > 0) return fromCode;
		const digits = Number(String(order.id ?? "").replace(/\D/g, ""));
		if (Number.isFinite(digits) && digits > 0) return digits;
		return null;
	};

	const handleOpenChat = (order: UserOrder) => {
		const detailsId = resolveOrderDetailsId(order);
		if (!detailsId) {
			navigate("/chat-expanded");
			return;
		}
		navigate("/chat-expanded", { state: { orderId: detailsId } });
	};

	useEffect(() => {
		setOrdersState(orders);
	}, [orders]);

	const handleOpenReportProblemModal = (order: UserOrder) => {
		if (reportingOrderId === order.id) return;
		setReportModalOrder(order);
		setReportReason("");
		setReportReasonError("");
	};

	const handleCloseReportProblemModal = () => {
		if (reportingOrderId) return;
		setReportModalOrder(null);
		setReportReason("");
		setReportReasonError("");
	};

	const handleSubmitReportProblem = async () => {
		if (!reportModalOrder) return;

		const trimmedReason = reportReason.trim();
		if (!trimmedReason) {
			setReportReasonError("Debes indicar un motivo del reporte.");
			return;
		}

		setReportingOrderId(reportModalOrder.id);
		try {
			await executePatchOrderStatus({
				endpoint: `/orders/${reportModalOrder.id}/status`,
				method: "PATCH",
				body: {
					status: "Soporte",
					reason: trimmedReason,
				},
			});

			setOrdersState((prev) =>
				prev.map((item) =>
					item.id === reportModalOrder.id
						? { ...item, status: "Soporte" }
						: item,
				),
			);

			handleCloseReportProblemModal();

			onNotify?.({
				title: "Pedido enviado a soporte",
				message: "El estado del pedido cambió a Soporte.",
				variant: "success",
			});
		} catch {
			onNotify?.({
				title: "No se pudo reportar",
				message:
					"Ocurrió un error al enviar el pedido a soporte. Intenta nuevamente.",
				variant: "error",
			});
		} finally {
			setReportingOrderId(null);
		}
	};

	const renderReportProblemButton = (order: UserOrder) => (
		<button
			type='button'
			onClick={(e) => {
				e.stopPropagation();
				handleOpenReportProblemModal(order);
			}}
			disabled={reportingOrderId === order.id}
			className='flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-red-300 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'
		>
			<AlertTriangle className='h-4 w-4' />
			{reportingOrderId === order.id ? "Reportando..." : "Reportar un problema"}
		</button>
	);

	const typeFilteredOrders =
		orderFilter === "orders"
			? ordersState.filter((o) => !o.isRequest)
			: orderFilter === "requests"
				? ordersState.filter((o) => o.isRequest)
				: ordersState;
	const showStatusFilter = orderFilter !== "requests";

	const statusOptions = useMemo(() => {
		const optionsMap = new Map<string, string>();

		typeFilteredOrders.forEach((order) => {
			const rawStatus = String(order.status ?? "").trim();
			if (!rawStatus) return;
			const normalized = normalizeOrderStatusFilterValue(rawStatus);
			if (!normalized || optionsMap.has(normalized)) return;
			optionsMap.set(normalized, rawStatus);
		});

		return Array.from(optionsMap.entries())
			.sort((a, b) => {
				const labelA = statusLabelByValue[a[1]] || a[1];
				const labelB = statusLabelByValue[b[1]] || b[1];
				return labelA.localeCompare(labelB, "es", {
					sensitivity: "base",
				});
			})
			.map(([value, raw]) => ({
				value,
				label: statusLabelByValue[raw] || raw,
			}));
	}, [typeFilteredOrders]);

	useEffect(() => {
		if (!statusFilter) return;
		const selectedStillExists = statusOptions.some(
			(option) => option.value === statusFilter,
		);
		if (!selectedStillExists) {
			setStatusFilter("");
		}
	}, [statusFilter, statusOptions]);

	useEffect(() => {
		if (showStatusFilter || !statusFilter) return;
		setStatusFilter("");
	}, [showStatusFilter, statusFilter]);

	const filteredOrders =
		showStatusFilter && statusFilter
			? typeFilteredOrders.filter(
					(order) =>
						normalizeOrderStatusFilterValue(order.status) === statusFilter,
				)
			: typeFilteredOrders;

	const filterButtons: { id: OrderFilter; label: string }[] = [
		{ id: "all", label: "Todos" },
		{ id: "orders", label: "Pedidos" },
		{ id: "requests", label: "Solicitudes" },
	];

	return (
		<div className='animate-in fade-in slide-in-from-bottom-4 duration-500'>
			<div className='mb-4'>
				<h2 className='text-xl font-bold text-gray-700'>Mis Ordenes</h2>
				<p className='text-sm text-gray-400'>
					Historial de servicios solicitados
				</p>
			</div>

			<div className='mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between'>
				<div className='flex flex-wrap gap-2'>
					{filterButtons.map((btn) => (
						<button
							key={btn.id}
							type='button'
							onClick={() => setOrderFilter(btn.id)}
							className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border cursor-pointer ${
								orderFilter === btn.id
									? "bg-primary text-white border-primary"
									: "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
							}`}
						>
							{btn.label}
						</button>
					))}
				</div>

				{showStatusFilter ? (
					<div className='w-full lg:w-72'>
						<label
							htmlFor='orders-status-filter'
							className='mb-1.5 block text-sm sm:text-base font-bold text-slate-700'
						>
							Estado
						</label>
						<select
							id='orders-status-filter'
							value={statusFilter}
							onChange={(event) => setStatusFilter(event.target.value)}
							className='h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-primary'
						>
							<option value=''>Todos los estados</option>
							{statusOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
				) : null}
			</div>

			{filteredOrders.length === 0 ? (
				<div className='rounded-2xl border border-gray-100 bg-white p-6 text-sm text-gray-500'>
					No tienes{" "}
					{orderFilter === "orders"
						? "pedidos"
						: orderFilter === "requests"
							? "solicitudes"
							: "ordenes"}{" "}
					{showStatusFilter && statusFilter
						? "con el estado seleccionado."
						: "registrados aún."}
				</div>
			) : (
				<div className='flex flex-col lg:flex-row gap-5'>
					{/* --- LEFT: Order Cards --- */}
					<div className='flex-1 flex flex-col gap-4 min-w-0'>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							{filteredOrders.map((order) => {
								const detailsId = Number(order.id);
								const canOpenDetails =
									!order.isRequest &&
									Number.isFinite(detailsId) &&
									detailsId > 0;
								return (
									<div
										key={order.id}
										onClick={() => {
											if (canOpenDetails)
												handleOpenOrderDetails(order);
										}}
										className={`text-left flex flex-col justify-between rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all ${
											canOpenDetails
												? "cursor-pointer hover:border-slate-200"
												: "cursor-default"
										}`}
									>
										<div>
											{/* Header: type indicator + status badge */}
											<div className='mb-3 flex items-center justify-between'>
												<span
													className={`text-xs font-medium ${order.isRequest ? "text-primary" : "text-slate-400"}`}
												>
													{order.isRequest
														? "Solicitud"
														: "Pedido"}
												</span>
												<span
													className={`inline-flex rounded-full border px-3 py-0.5 text-xs font-semibold ${statusColors[order.status] || "bg-slate-50 text-slate-700 border-slate-200"}`}
												>
													{statusLabelByValue[order.status] ||
														order.status}
												</span>
											</div>

											{/* Número de Pedido / Solicitud */}
											<div className='mb-4 space-y-1'>
												<p className='text-xs text-slate-400'>
													{order.isRequest
														? "Número de Solicitud"
														: "Número de Pedido"}
												</p>
												<p className='text-lg font-bold text-primary'>
													{order.numeroPedido}
												</p>
											</div>

											{/* Info rows */}
											<div className='space-y-2.5 text-sm'>
												<div className='flex items-center justify-between'>
													<span className='text-slate-400 flex items-center gap-1.5'>
														<Calendar className='h-3.5 w-3.5' />
														{order.isRequest
															? "Fecha de Envío"
															: "Fecha de Transacción"}
													</span>
													<span className='text-slate-700 text-xs'>
														{order.fecha}
													</span>
												</div>

												<div className='flex items-center justify-between'>
													<span className='text-slate-400 flex items-center gap-1.5'>
														<FileText className='h-3.5 w-3.5' />
														ID de Transacción
													</span>
													<span className='text-slate-700 text-xs'>
														{order.idTransaccion ??
															order.numeroPedido}
													</span>
												</div>
												{order.isRequest &&
													order.fechaRespuesta && (
														<div className='flex items-center justify-between'>
															<span className='text-slate-400 flex items-center gap-1.5'>
																<Calendar className='h-3.5 w-3.5' />
																{order.status ===
																"Solicitud Aceptada"
																	? "Fecha de Aceptación"
																	: "Fecha de Rechazo"}
															</span>
															<span className='text-slate-700 text-xs'>
																{order.fechaRespuesta}
															</span>
														</div>
													)}

												{order.status === "Pago Rechazado" &&
												order.paymentRejectionReason ? (
													<div className='rounded-xl border border-red-100 bg-red-50 px-3 py-2'>
														<p className='text-[11px] font-semibold text-red-700'>
															Conciliación rechazada
														</p>
														<p className='mt-0.5 text-xs text-red-600'>
															{order.paymentRejectionReason}
														</p>
													</div>
												) : null}

												{!order.isRequest && (
													<div className='flex items-center justify-between'>
														<span className='text-slate-400'>
															Categoría
														</span>
														<span className='text-slate-700'>
															{order.categoria ?? "-"}
														</span>
													</div>
												)}

												<div className='flex items-center justify-between'>
													<span className='text-slate-400'>
														Proveedor
													</span>
													<span className='text-slate-700 font-medium'>
														{order.providerName}
													</span>
												</div>
											</div>

											{/* Fecha Pauta */}

											{/* Ubicación */}
											{order.ubicacion &&
											order.ubicacion !== "-" ? (
												<div className='mt-3 border-t border-slate-100 pt-3'>
													<p className='text-xs font-medium text-slate-400 flex items-center gap-1'>
														<MapPin className='h-3 w-3' />
														Ubicación
													</p>
													<p className='mt-0.5 text-sm text-slate-700'>
														{order.ubicacion}
													</p>
												</div>
											) : null}

											{order.fechaPauta ? (
												<div className='mt-4 border-t border-slate-100 pt-3'>
													<p className='text-xs font-medium text-slate-400'>
														Fecha Pautada
													</p>
													<p className='mt-0.5 text-sm text-slate-700'>
														{order.fechaPauta}
													</p>
												</div>
											) : null}

											{/* Hora + Duración */}
											<div className='mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3'>
												<div>
													<p className='text-xs text-slate-400 flex items-center gap-1'>
														<Clock className='h-3 w-3' />
														Hora
													</p>
													<p className='text-sm text-slate-700'>
														{order.hora || "-"}
													</p>
												</div>
												<div>
													<p className='text-xs text-slate-400 flex items-center gap-1'>
														<Clock className='h-3 w-3' />
														Duración Estimada
													</p>
													<p className='text-sm text-slate-700'>
														{order.duracionEstimada || "-"}
													</p>
												</div>
											</div>
											{/* Budget info for requests */}
											{order.isRequest && order.budget && (
												<div className='mt-3 border-t border-slate-100 pt-3'>
													<p className='text-xs font-medium text-slate-400'>
														Presupuesto Estimado
													</p>
													<p className='mt-0.5 text-sm font-semibold text-primary'>
														{order.budget}
													</p>
												</div>
											)}
										</div>

										{/* Footer buttons */}
										<div className='mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 border-t border-slate-100 pt-4'>
											{/* --- Request-specific buttons --- */}
											{order.status === "Solicitud Pendiente" && (
												<span className='w-full text-left py-1 text-xs font-semibold text-[#894C00]'>
													Esperando al proveedor
												</span>
											)}
											{order.status === "Cotización Recibida" && (
												<>
													<span className='flex-1 text-center rounded-full border border-red-200 bg-red-50 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 transition cursor-pointer'>
														Rechazar
													</span>
													<span className='flex-1 text-center rounded-full bg-primary py-2 text-xs font-semibold text-white hover:bg-primary-hover transition shadow-sm shadow-sky-200 cursor-pointer'>
														Aceptar Cotización
													</span>
												</>
											)}
											{order.status === "Solicitud Aceptada" && (
												<span className='w-full text-left py-1 text-xs font-semibold text-[#008235]'>
													Solicitud aceptada
												</span>
											)}
											{order.status === "Solicitud Rechazada" && (
												<span className='w-full text-left py-1 text-xs font-semibold text-[#E93A1F]'>
													Solicitud rechazada
												</span>
											)}
											{order.status === "Solicitud Cancelada" && (
												<span className='flex-1 text-center rounded-full border border-red-200 bg-red-50 py-2 text-xs font-semibold text-red-600'>
													Cancelada
												</span>
											)}

											{/* --- Order-specific buttons --- */}
											{order.status === "Dialogando" && (
												<>
													{renderReportProblemButton(order)}
													<button
														type='button'
														onClick={(event) => {
															event.stopPropagation();
															handleOpenChat(order);
														}}
														className='flex-1 text-center rounded-full bg-primary py-2 text-xs font-semibold text-white hover:bg-primary-hover transition shadow-sm shadow-sky-200 cursor-pointer'
													>
														Ir al chat
													</button>
												</>
											)}
											{order.status === "En Espera" && (
												<>
													{renderReportProblemButton(order)}
													<button
														type='button'
														onClick={(event) => {
															event.stopPropagation();
															handleOpenChat(order);
														}}
														className='flex-1 text-center rounded-full bg-primary py-2 text-xs font-semibold text-white hover:bg-primary-hover transition shadow-sm shadow-sky-200 cursor-pointer'
													>
														Ir al chat
													</button>
												</>
											)}
											{order.status === "Pago Rechazado" && (
												<span
													className='flex-1 text-center rounded-full bg-primary py-2 text-xs font-semibold text-white hover:bg-primary-hover transition shadow-sm shadow-sky-200 cursor-pointer'
													onClick={(event) => {
														event.stopPropagation();
														handleOpenPaymentRegistration(
															order,
														);
													}}
												>
													Volver a registrar pago
												</span>
											)}
											{order.status === "En Progreso" &&
												renderReportProblemButton(order)}
											{order.status === "Por pagar" && (
												<button
													type='button'
													onClick={(event) => {
														event.stopPropagation();
														handleOpenChat(order);
													}}
													className='flex-1 text-center rounded-full bg-primary py-2 text-xs font-semibold text-white hover:bg-primary-hover transition shadow-sm shadow-sky-200 cursor-pointer'
												>
													Ir al chat
												</button>
											)}
											{order.status === "Completado" ||
											order.status === "Pagado" ? (
												<span className='flex-1 text-center rounded-full border border-slate-200 bg-slate-50 py-2 text-xs font-semibold text-slate-600'>
													Completado
												</span>
											) : null}
											{order.status === "Cancelado" && (
												<span className='flex-1 text-center rounded-full border border-red-200 bg-red-50 py-2 text-xs font-semibold text-red-600'>
													Cancelado
												</span>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			)}

			<ReportReasonModal
				open={Boolean(reportModalOrder)}
				reason={reportReason}
				error={reportReasonError}
				loading={Boolean(reportingOrderId)}
				onReasonChange={(value) => {
					setReportReason(value);
					if (reportReasonError) setReportReasonError("");
				}}
				onClose={handleCloseReportProblemModal}
				onSubmit={() => {
					void handleSubmitReportProblem();
				}}
			/>
		</div>
	);
};

// ----------------------------------------------------------------------
// 3. SUB-COMPONENTE: SEGURIDAD
// ----------------------------------------------------------------------
const Security = ({
	onChangePassword,
	isUpdating,
	onDeleteAccount,
	isDeletingAccount,
}: {
	onChangePassword: (payload: {
		currentPassword: string;
		newPassword: string;
		confirmPassword: string;
		clear: () => void;
	}) => void;
	isUpdating: boolean;
	onDeleteAccount: () => Promise<void>;
	isDeletingAccount: boolean;
}) => {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const clearPasswords = () => {
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
	};

	return (
		<div className='space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500'>
			<div className='mb-4'>
				<h2 className='text-xl font-bold text-gray-700'>Seguridad</h2>
				<p className='text-sm text-gray-400'>
					Gestiona tu contraseña y configuración de seguridad
				</p>
			</div>

			{/* Cambiar Contraseña */}
			<div className='bg-gray-50/50 rounded-2xl p-4 sm:p-6 border border-gray-100'>
				<div className='flex items-center gap-3 mb-6'>
					<div className='p-2 bg-secondary rounded-lg text-primary'>
						<Lock size={20} />
					</div>
					<div>
						<h3 className='font-semibold text-gray-700'>
							Cambiar Contraseña
						</h3>
						<p className='text-xs text-gray-400'>
							Actualiza tu contraseña periódicamente
						</p>
					</div>
				</div>

				<div className='space-y-4'>
					<div className='space-y-1'>
						<label
							htmlFor='current-password'
							className='text-xs font-semibold text-gray-500 ml-1'
						>
							Contraseña Actual
						</label>
						<div className='relative'>
							<input
								id='current-password'
								type='password'
								placeholder='Ingresa tu contraseña actual'
								value={currentPassword}
								onChange={(e) => setCurrentPassword(e.target.value)}
								className='w-full border border-gray-200 rounded-lg pl-4 pr-10 py-3 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all'
							/>
							<Eye className='absolute right-3 top-3.5 text-gray-400 w-5 h-5 cursor-pointer hover:text-gray-600' />
						</div>
					</div>

					<div className='space-y-1'>
						<label
							htmlFor='new-password'
							className='text-xs font-semibold text-gray-500 ml-1'
						>
							Nueva Contraseña
						</label>
						<div className='relative'>
							<input
								id='new-password'
								type='password'
								placeholder='Ingresa tu nueva contraseña'
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								className='w-full border border-gray-200 rounded-lg pl-4 pr-10 py-3 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all'
							/>
							<Eye className='absolute right-3 top-3.5 text-gray-400 w-5 h-5 cursor-pointer hover:text-gray-600' />
						</div>
					</div>

					<div className='space-y-1'>
						<label
							htmlFor='confirm-password'
							className='text-xs font-semibold text-gray-500 ml-1'
						>
							Confirmar Nueva Contraseña
						</label>
						<div className='relative'>
							<input
								id='confirm-password'
								type='password'
								placeholder='Confirma tu nueva contraseña'
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className='w-full border border-gray-200 rounded-lg pl-4 pr-10 py-3 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all'
							/>
							<Eye className='absolute right-3 top-3.5 text-gray-400 w-5 h-5 cursor-pointer hover:text-gray-600' />
						</div>
					</div>

					<button
						onClick={() =>
							onChangePassword({
								currentPassword,
								newPassword,
								confirmPassword,
								clear: clearPasswords,
							})
						}
						disabled={isUpdating}
						className='w-full mt-2 bg-primary text-white font-semibold py-3 rounded-xl hover:bg-primary-hover transition-colors shadow-lg  cursor-pointer'
					>
						{isUpdating ? "Actualizando..." : "Actualizar Contraseña"}
					</button>
				</div>
			</div>

			{/* Eliminar cuenta */}
			<div className='bg-rose-50 rounded-2xl p-4 sm:p-6 border border-rose-100'>
				<div className='flex items-center gap-3 mb-4'>
					<div className='p-2 bg-rose-100 rounded-lg text-rose-600'>
						<Trash2 size={20} />
					</div>
					<div>
						<h3 className='font-semibold text-rose-700'>Eliminar Cuenta</h3>
						<p className='text-xs text-rose-400'>
							Esta acción es permanente e irreversible
						</p>
					</div>
				</div>
				<p className='text-sm text-rose-600 mb-4'>
					Al eliminar tu cuenta perderás acceso a todos tus datos, historial de
					pedidos y configuraciones. No podrás recuperarla.
				</p>
				<button
					type='button'
					onClick={() => void onDeleteAccount()}
					disabled={isDeletingAccount}
					className='w-full py-3 rounded-xl bg-rose-600 text-white font-semibold text-sm hover:bg-rose-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'
				>
					{isDeletingAccount ? "Eliminando cuenta..." : "Eliminar mi cuenta"}
				</button>
			</div>

			{/* 2FA */}
			{/* Para futuras generaciones */}
			{/* <div className="bg-white rounded-2xl p-6 border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-secondary rounded-full text-primary">
            <Shield size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700">
              Autenticación de Dos Factores
            </h3>
            <p className="text-sm text-gray-400">
              Agrega una capa extra de seguridad
            </p>
          </div>
        </div>
        <button className="px-6 py-2 rounded-full border border-secondary text-primary font-medium hover:bg-secondary-hover transition-colors whitespace-nowrap">
          Activar
        </button>
      </div> */}
		</div>
	);
};

// ----------------------------------------------------------------------
// COMPONENTE PRINCIPAL
// ----------------------------------------------------------------------
export const UserDetails = ({
	profile,
	orders = [],
	addresses = [],
	onAddressesChange,
	initialTab,
}: {
	profile?: UserPersonalInfo;
	orders?: UserOrder[];
	addresses?: ApiAddress[];
	onAddressesChange?: () => void;
	initialTab?: string;
}) => {
	const [activeTab, setActiveTab] = useState("personal");
	const [isSavingPersonalInfo, setIsSavingPersonalInfo] = useState(false);
	const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
	const [editingAddress, setEditingAddress] = useState<UserAddressRow | null>(null);
	const [isSavingAddress, setIsSavingAddress] = useState(false);
	const [addressForm, setAddressForm] = useState({
		state: "",
		city: "",
		street: "",
		reference: "",
	});
	const [addressErrors, setAddressErrors] = useState<{
		state?: string;
		city?: string;
		street?: string;
		reference?: string;
	}>({});
	const [stateSuggestions, setStateSuggestions] = useState<AdministrativeSuggestion[]>(
		[],
	);
	const [citySuggestions, setCitySuggestions] = useState<AdministrativeSuggestion[]>(
		[],
	);
	const [stateSuggestStatus, setStateSuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const [citySuggestStatus, setCitySuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const stateSuggestAbortRef = useRef<AbortController | null>(null);
	const citySuggestAbortRef = useRef<AbortController | null>(null);
	const stateSuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);
	const citySuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);
	const [localProfile, setLocalProfile] = useState<UserPersonalInfo>(
		profile ?? personalFallback,
	);
	const resolvedProfile = localProfile;
	const clientIdFromProfile =
		typeof resolvedProfile.clients?.id === "number"
			? resolvedProfile.clients.id
			: null;
	const { execute: executePatchUser } = useFetch<unknown, Record<string, unknown>>({
		endpoint: "/users/1",
		method: "PATCH",
		auto: false,
	});
	const { changePassword, loading: isChangingPassword } = useChangeUserPassword(
		resolvedProfile.id,
	);
	const { execute: executePatchAddress } = useFetch<unknown, Record<string, unknown>>({
		endpoint: "/addresses/1",
		method: "PATCH",
		auto: false,
	});
	const { execute: executeCreateAddress } = useFetch<unknown, Record<string, unknown>>({
		endpoint: "/addresses",
		method: "POST",
		auto: false,
	});
	const { execute: executeAddressStatusPatch } = useFetch<unknown>({
		endpoint: "/addresses/1",
		method: "PATCH",
		auto: false,
	});
	const [isDeletingAccount, setIsDeletingAccount] = useState(false);
	const { execute: executeDeleteAccount } = useFetch<unknown, Record<string, unknown>>({
		endpoint: "/banned-user",
		method: "POST",
		auto: false,
	});
	const [alertState, setAlertState] = useState<AlertConfig & { open: boolean }>({
		open: false,
		title: "",
		message: "",
		variant: "info",
		confirmText: "Entendido",
	});
	const [confirmState, setConfirmState] = useState<ConfirmConfig & { open: boolean }>({
		open: false,
		title: "",
		message: "",
		variant: "primary",
		confirmLabel: "Confirmar",
		cancelLabel: "Cancelar",
	});
	const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);

	const showAlert = (config: AlertConfig) => {
		setAlertState({
			open: true,
			title: config.title,
			message: config.message,
			variant: config.variant ?? "info",
			confirmText: config.confirmText ?? "Entendido",
		});
	};

	const handleCloseAlert = () => {
		setAlertState((prev) => ({ ...prev, open: false }));
	};

	const requestConfirmation = (config: ConfirmConfig) =>
		new Promise<boolean>((resolve) => {
			confirmResolverRef.current = resolve;
			setConfirmState({
				open: true,
				title: config.title,
				message: config.message,
				variant: config.variant ?? "primary",
				confirmLabel: config.confirmLabel,
				cancelLabel: config.cancelLabel,
			});
		});

	const resolveConfirm = (value: boolean) => {
		confirmResolverRef.current?.(value);
		confirmResolverRef.current = null;
		setConfirmState((prev) => ({ ...prev, open: false }));
	};

	const handleConfirmAccept = () => {
		resolveConfirm(true);
	};

	const handleConfirmCancel = () => {
		resolveConfirm(false);
	};

	useEffect(() => {
		setLocalProfile(profile ?? personalFallback);
	}, [profile]);

	const handleSave = async (payload: PersonalInfoSavePayload) => {
		const confirmed = await requestConfirmation({
			title: "Guardar",
			message: "¿Deseas guardar los datos del perfil?",
			confirmLabel: "Sí",
			cancelLabel: "No",
		});
		if (!confirmed) return;

		if ((resolvedProfile.id ?? 0) <= 0) {
			showAlert({
				title: "Usuario inválido",
				message: "No se pudo identificar el usuario para actualizar el perfil.",
				variant: "error",
			});
			return;
		}

		setIsSavingPersonalInfo(true);
		try {
			const normalizedFirstName = payload.firstName.trim();
			const normalizedLastName = payload.lastName.trim();
			const normalizedName = `${normalizedFirstName} ${normalizedLastName}`.trim();
			const normalizedEmail = payload.email.trim();
			const normalizedPhone = payload.phone.trim();

			await executePatchUser({
				endpoint: `/users/${resolvedProfile.id}`,
				method: "PATCH",
				body: {
					names: normalizedFirstName,
					last_names: normalizedLastName,
					email: normalizedEmail,
					phone: normalizedPhone,
					...(payload.dateOfBirth !== undefined ? { date_of_birth: payload.dateOfBirth } : {}),
					...(payload.gender !== undefined ? { gender_user: payload.gender } : {}),
				},
			});

			setLocalProfile((prev) => ({
				...prev,
				...payload,
				name: normalizedName,
				email: normalizedEmail,
				phone: normalizedPhone,
			}));

			showAlert({
				title: "Información actualizada",
				message: "Tus datos personales se guardaron correctamente.",
				variant: "success",
			});
		} catch (error: unknown) {
			const message =
				error instanceof Error
					? error.message
					: "No se pudo actualizar la información personal.";
			showAlert({
				title: "Error",
				message,
				variant: "error",
			});
		} finally {
			setIsSavingPersonalInfo(false);
		}
	};

	const handleChangePassword = async ({
		currentPassword,
		newPassword,
		confirmPassword,
		clear,
	}: {
		currentPassword: string;
		newPassword: string;
		confirmPassword: string;
		clear: () => void;
	}) => {
		if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
			showAlert({
				title: "Campos requeridos",
				message: "Completa todos los campos para actualizar la contraseña.",
				variant: "warning",
			});
			return;
		}

		if (newPassword !== confirmPassword) {
			showAlert({
				title: "Contraseñas no coinciden",
				message: "La nueva contraseña y su confirmación deben ser iguales.",
				variant: "warning",
			});
			return;
		}

		if (currentPassword.trim() === newPassword.trim()) {
			showAlert({
				title: "Contraseña igual",
				message: "La nueva contraseña no puede ser igual a la contraseña actual.",
				variant: "warning",
			});
			return;
		}

		if ((resolvedProfile.id ?? 0) <= 0) {
			showAlert({
				title: "Usuario inválido",
				message:
					"No se pudo identificar el usuario para actualizar la contraseña.",
				variant: "error",
			});
			return;
		}

		const confirmed = await requestConfirmation({
			title: "Cambio de Contraseña",
			message: "¿Estás seguro de que quieres modificar tu contraseña?",
			confirmLabel: "Sí",
			cancelLabel: "No",
		});
		if (!confirmed) return;

		try {
			await changePassword(currentPassword, newPassword);
			clear();
			showAlert({
				title: "Contraseña actualizada",
				message: "Tu contraseña fue actualizada correctamente.",
				variant: "success",
			});
		} catch (error: unknown) {
			const message =
				error instanceof Error
					? error.message
					: "No se pudo actualizar la contraseña. Intenta nuevamente.";
			showAlert({
				title: "Error",
				message,
				variant: "error",
			});
		}
	};

	const handleDeleteAccount = async () => {
		if ((resolvedProfile.id ?? 0) <= 0) {
			showAlert({
				title: "Usuario inválido",
				message: "No se pudo identificar tu cuenta para eliminarla.",
				variant: "error",
			});
			return;
		}

		const first = await requestConfirmation({
			title: "Eliminar cuenta",
			message:
				"¿Estás seguro de que deseas eliminar tu cuenta? Esta acción es permanente e irreversible.",
			confirmLabel: "Sí, eliminar",
			cancelLabel: "Cancelar",
			variant: "danger",
		});
		if (!first) return;

		const second = await requestConfirmation({
			title: "Confirmar eliminación",
			message:
				"Esta es tu última oportunidad. Una vez eliminada no podrás recuperar tu cuenta ni acceder a ella. ¿Confirmas la eliminación permanente?",
			confirmLabel: "Confirmar eliminación",
			cancelLabel: "Cancelar",
			variant: "danger",
		});
		if (!second) return;

		setIsDeletingAccount(true);
		try {
			await executeDeleteAccount({
				body: {
					user_id: resolvedProfile.id,
					reason: "Cuenta eliminada por solicitud del usuario",
				},
			});
			await logout();
			window.location.replace("/login");
		} catch (error: unknown) {
			const message =
				error instanceof Error
					? error.message
					: "No se pudo eliminar la cuenta. Intenta nuevamente.";
			showAlert({ title: "Error", message, variant: "error" });
		} finally {
			setIsDeletingAccount(false);
		}
	};

	const mappedAddresses = useMemo<UserAddressRow[]>(() => {
		return addresses
			.map((address) => {
				const id =
					typeof address.id === "number" && Number.isFinite(address.id)
						? address.id
						: null;
				if (!id) return null;

				const state =
					(
						address as ApiAddress & { name_state?: string | null }
					).name_state?.trim() ||
					address.states?.name_state?.trim() ||
					address.states?.name_complete?.trim() ||
					"";

				const city =
					(
						address as ApiAddress & { name_city?: string | null }
					).name_city?.trim() ||
					address.municipalities?.name_municipality?.trim() ||
					address.municipalities?.name_complete?.trim() ||
					address.parishes?.name_parish?.trim() ||
					"";

				const street = address.street_address?.trim() || "";
				const reference = address.reference_address?.trim() || "";

				if (!state && !city && !street && !reference) return null;

				return {
					id,
					state: state || "-",
					city: city || "-",
					street: street || "-",
					reference: reference || "-",
					isActive: address.is_active !== false,
				};
			})
			.filter((item): item is UserAddressRow => Boolean(item));
	}, [addresses]);

	const resetAddressSuggestions = () => {
		stateSuggestAbortRef.current?.abort();
		citySuggestAbortRef.current?.abort();
		if (stateSuggestDebounceRef.current) {
			window.clearTimeout(stateSuggestDebounceRef.current);
			stateSuggestDebounceRef.current = null;
		}
		if (citySuggestDebounceRef.current) {
			window.clearTimeout(citySuggestDebounceRef.current);
			citySuggestDebounceRef.current = null;
		}
		setStateSuggestions([]);
		setCitySuggestions([]);
		setStateSuggestStatus("idle");
		setCitySuggestStatus("idle");
	};

	useEffect(() => {
		return () => {
			resetAddressSuggestions();
		};
	}, []);

	const handleStateInput = (nextValue: string) => {
		setAddressForm((prev) => ({
			...prev,
			state: nextValue,
		}));
		setAddressErrors((prev) => ({ ...prev, state: undefined }));

		stateSuggestAbortRef.current?.abort();
		if (stateSuggestDebounceRef.current) {
			window.clearTimeout(stateSuggestDebounceRef.current);
			stateSuggestDebounceRef.current = null;
		}

		setStateSuggestStatus("idle");
		const query = nextValue.trim();
		if (query.length < 2) {
			setStateSuggestions([]);
			return;
		}

		stateSuggestDebounceRef.current = window.setTimeout(async () => {
			setStateSuggestStatus("loading");
			const controller = new AbortController();
			stateSuggestAbortRef.current = controller;
			try {
				const results = await searchAdministrativeNominatim(query, {
					level: "state",
					country: "Venezuela",
					limit: 8,
					signal: controller.signal,
				});
				setStateSuggestions(results);
				setStateSuggestStatus("idle");
			} catch {
				setStateSuggestStatus("error");
			}
		}, 300);
	};

	const handleCityInput = (nextValue: string) => {
		setAddressForm((prev) => ({
			...prev,
			city: nextValue,
		}));
		setAddressErrors((prev) => ({ ...prev, city: undefined }));

		citySuggestAbortRef.current?.abort();
		if (citySuggestDebounceRef.current) {
			window.clearTimeout(citySuggestDebounceRef.current);
			citySuggestDebounceRef.current = null;
		}

		setCitySuggestStatus("idle");
		const query = nextValue.trim();
		if (query.length < 2) {
			setCitySuggestions([]);
			return;
		}

		citySuggestDebounceRef.current = window.setTimeout(async () => {
			setCitySuggestStatus("loading");
			const controller = new AbortController();
			citySuggestAbortRef.current = controller;
			try {
				const results = await searchAdministrativeNominatim(query, {
					level: "city",
					stateContext: addressForm.state,
					country: "Venezuela",
					limit: 8,
					signal: controller.signal,
				});
				setCitySuggestions(results);
				setCitySuggestStatus("idle");
			} catch {
				setCitySuggestStatus("error");
			}
		}, 300);
	};

	const selectStateSuggestion = (suggestion: AdministrativeSuggestion) => {
		stateSuggestAbortRef.current?.abort();
		if (stateSuggestDebounceRef.current) {
			window.clearTimeout(stateSuggestDebounceRef.current);
			stateSuggestDebounceRef.current = null;
		}
		setAddressForm((prev) => ({ ...prev, state: suggestion.value }));
		setAddressErrors((prev) => ({ ...prev, state: undefined }));
		setStateSuggestions([]);
		setStateSuggestStatus("idle");
	};

	const selectCitySuggestion = (suggestion: AdministrativeSuggestion) => {
		citySuggestAbortRef.current?.abort();
		if (citySuggestDebounceRef.current) {
			window.clearTimeout(citySuggestDebounceRef.current);
			citySuggestDebounceRef.current = null;
		}
		setAddressForm((prev) => ({ ...prev, city: suggestion.value }));
		setAddressErrors((prev) => ({ ...prev, city: undefined }));
		setCitySuggestions([]);
		setCitySuggestStatus("idle");
	};

	const openCreateAddressModal = () => {
		resetAddressSuggestions();
		setEditingAddress(null);
		setAddressForm({ state: "", city: "", street: "", reference: "" });
		setAddressErrors({});
		setIsAddressModalOpen(true);
	};

	const openEditAddressModal = (address: UserAddressRow) => {
		resetAddressSuggestions();
		setEditingAddress(address);
		setAddressForm({
			state: address.state || "",
			city: address.city || "",
			street: address.street || "",
			reference: address.reference || "",
		});
		setAddressErrors({});
		setIsAddressModalOpen(true);
	};

	const closeAddressModal = () => {
		resetAddressSuggestions();
		setIsAddressModalOpen(false);
		setEditingAddress(null);
		setAddressErrors({});
	};

	const handleSaveAddress = async () => {
		const state = addressForm.state.trim();
		const city = addressForm.city.trim();
		const street = addressForm.street.trim();
		const reference = addressForm.reference.trim();
		const nextErrors: {
			state?: string;
			city?: string;
			street?: string;
		} = {};

		if (!state) nextErrors.state = "El estado es obligatorio";
		if (!city) nextErrors.city = "La ciudad es obligatoria";
		if (!street) nextErrors.street = "La calle es obligatoria";

		if (Object.keys(nextErrors).length > 0) {
			setAddressErrors(nextErrors);
			return;
		}

		if ((resolvedProfile.id ?? 0) <= 0 && !editingAddress) {
			showAlert({
				title: "Usuario inválido",
				message: "No se pudo identificar al usuario para guardar la dirección.",
				variant: "error",
			});
			return;
		}

		setIsSavingAddress(true);
		try {
			if (editingAddress) {
				await executePatchAddress({
					endpoint: `/addresses/${editingAddress.id}`,
					method: "PATCH",
					body: {
						name_country: "Venezuela",
						name_state: state,
						name_city: city,
						name_address: `${city}, ${state}`,
						street_address: street,
						reference_address: reference || null,
					},
				});
			} else {
				const createdAddress = await executeCreateAddress({
					endpoint: "/addresses",
					method: "POST",
					body: {
						user_id: resolvedProfile.id,
						name_country: "Venezuela",
						name_state: state,
						name_city: city,
						name_address: `${city}, ${state}`,
						street_address: street,
						...(reference ? { reference_address: reference } : {}),
					},
				});
				const created = ((createdAddress as { address?: { id?: number } })
					?.address ?? (createdAddress as { id?: number })) as
					| { id?: number }
					| undefined;
				if (typeof created?.id === "number") {
					await executeAddressStatusPatch({
						endpoint: `/addresses/${created.id}/reactivate`,
						method: "PATCH",
						body: {},
					}).catch(() => null);
				}
			}

			onAddressesChange?.();
			closeAddressModal();
		} catch {
			showAlert({
				title: "Error",
				message: "No se pudo guardar la dirección. Intenta de nuevo.",
				variant: "error",
			});
		} finally {
			setIsSavingAddress(false);
		}
	};

	const tabs = useMemo(
		() => [
			{ id: "personal", label: "Datos Personales" },
			{ id: "addresses", label: "Mis Direcciones" },
			{ id: "orders", label: "Mis Ordenes" },
			{ id: "security", label: "Seguridad" },
			{ id: "favoritos", label: "Favoritos" },
		],
		[],
	);

	useEffect(() => {
		if (!initialTab) return;
		const validTabs = new Set(tabs.map((tab) => tab.id));
		if (!validTabs.has(initialTab)) return;
		setActiveTab(initialTab);
	}, [initialTab, tabs]);

	return (
		<div className='w-full mx-auto p-3 sm:p-4 md:p-8'>
			<div className='bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden min-h-150'>
				{/* HEADER DE PESTAÑAS */}
				<div className='px-4 sm:px-6 pt-4 sm:pt-6 border-b border-gray-300 flex items-end justify-between gap-3'>
					<div className='flex gap-4 sm:gap-8 overflow-x-auto scrollbar-hide'>
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`cursor-pointer pb-3 sm:pb-4 text-[13px] sm:text-sm md:text-base font-medium transition-all relative whitespace-nowrap ${
									activeTab === tab.id
										? "text-primary"
										: "text-gray-400 hover:text-gray-600"
								}`}
							>
								{tab.label}
								{activeTab === tab.id && (
									<span className='absolute bottom-0 left-0 w-full h-1 bg-primary rounded-t-full' />
								)}
							</button>
						))}
					</div>
				</div>

				{/* CONTENIDO */}
				<div className='p-4 sm:p-6 md:p-8 bg-white'>
					{activeTab === "personal" && (
						<PersonalData
							profile={resolvedProfile}
							onSavePersonalInfo={handleSave}
							isSaving={isSavingPersonalInfo}
							onNotify={showAlert}
						/>
					)}
					{activeTab === "addresses" && (
						<AddressCardsBoard
							title='Direcciones guardadas'
							subtitle='Gestiona tus ubicaciones registradas por ciudad y estado'
							addLabel='Agregar dirección'
							onAdd={openCreateAddressModal}
							actionsDisabled={isSavingAddress}
							cards={mappedAddresses.map((address, index) => ({
								key: address.id,
								title: `Dirección ${index + 1}`,
								isActive: address.isActive,
								rows: [
									{ label: "Estado", value: address.state || "-" },
									{ label: "Ciudad", value: address.city || "-" },
									{ label: "Calle", value: address.street || "-" },
									{
										label: "Referencia",
										value: address.reference || "-",
									},
								],
								onEdit: () => openEditAddressModal(address),
								onToggleActive: async () => {
									const shouldDeactivate = address.isActive;
									const confirmed = await requestConfirmation({
										title: shouldDeactivate
											? "Desactivar dirección"
											: "Activar dirección",
										message: shouldDeactivate
											? "¿Deseas desactivar esta dirección? Podrás activarla luego."
											: "¿Deseas activar nuevamente esta dirección?",
										confirmLabel: shouldDeactivate
											? "Sí, desactivar"
											: "Sí, activar",
										cancelLabel: "Cancelar",
										variant: shouldDeactivate ? "danger" : "success",
									});
									if (!confirmed) return;
									try {
										await executeAddressStatusPatch({
											endpoint: shouldDeactivate
												? `/addresses/${address.id}/status`
												: `/addresses/${address.id}/reactivate`,
											method: "PATCH",
											body: {},
										});
										onAddressesChange?.();
									} catch (error) {
										const rawMessage =
											error instanceof Error
												? error.message
												: "No se pudo actualizar el estado de la dirección.";
										const isAddressInUse = rawMessage
											.toLowerCase()
											.includes(
												"asociada a una solicitud o pedido",
											);
										showAlert({
											title: isAddressInUse
												? "Dirección en uso"
												: "Error",
											message: isAddressInUse
												? "No puedes desactivar esta dirección porque está asociada a una solicitud o pedido."
												: "No se pudo actualizar el estado de la dirección. Intenta de nuevo.",
											variant: isAddressInUse ? "warning" : "error",
										});
									}
								},
								toggleActiveLabel: "Desactivar",
								toggleInactiveLabel: "Activar",
							}))}
						/>
					)}
					{activeTab === "orders" && (
						<MyOrders
							orders={orders}
							onNotify={showAlert}
							//onRequestConfirm={requestConfirmation}
						/>
					)}
					{activeTab === "security" && (
						<Security
							onChangePassword={handleChangePassword}
							isUpdating={isChangingPassword}
							onDeleteAccount={handleDeleteAccount}
							isDeletingAccount={isDeletingAccount}
						/>
					)}
					{activeTab === "favoritos" && (
						<ClientFavorites clientIdProp={clientIdFromProfile} />
					)}
				</div>
			</div>
			{isAddressModalOpen ? (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6'>
					<div className='w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xl'>
						<div className='mb-4 flex items-center justify-between'>
							<h3 className='text-base sm:text-lg font-semibold text-slate-800'>
								{editingAddress
									? "Editar dirección"
									: "Agregar dirección"}
							</h3>
							<button
								type='button'
								onClick={closeAddressModal}
								className='cursor-pointer rounded-full p-1 text-slate-500 transition hover:bg-slate-100'
								aria-label='Cerrar modal de dirección'
							>
								<X className='h-4 w-4' />
							</button>
						</div>

						<div className='space-y-4'>
							<div>
								<label className='mb-1 block text-sm font-medium text-slate-700'>
									Estado
								</label>
								<div className='relative'>
									<input
										type='text'
										value={addressForm.state}
										onChange={(e) => handleStateInput(e.target.value)}
										onBlur={() => {
											window.setTimeout(
												() => setStateSuggestions([]),
												150,
											);
										}}
										onKeyDown={(event) => {
											if (event.key === "Escape")
												setStateSuggestions([]);
										}}
										className='h-10 sm:h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
										placeholder='Ej: Miranda'
									/>
									{stateSuggestions.length > 0 ? (
										<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-md'>
											{stateSuggestions.map((suggestion) => (
												<li
													key={`state-${suggestion.value}-${suggestion.label}`}
													className='border-b border-slate-100 last:border-b-0'
												>
													<button
														type='button'
														className='w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
														onMouseDown={(event) => {
															event.preventDefault();
															selectStateSuggestion(
																suggestion,
															);
														}}
													>
														{suggestion.label}
													</button>
												</li>
											))}
										</ul>
									) : null}
								</div>
								{stateSuggestStatus === "loading" ? (
									<p className='mt-1 text-xs text-slate-500'>
										Buscando sugerencias...
									</p>
								) : null}
								{addressErrors.state ? (
									<p className='mt-1 text-xs text-red-500'>
										{addressErrors.state}
									</p>
								) : null}
							</div>

							<div>
								<label className='mb-1 block text-sm font-medium text-slate-700'>
									Ciudad
								</label>
								<div className='relative'>
									<input
										type='text'
										value={addressForm.city}
										onChange={(e) => handleCityInput(e.target.value)}
										onBlur={() => {
											window.setTimeout(
												() => setCitySuggestions([]),
												150,
											);
										}}
										onKeyDown={(event) => {
											if (event.key === "Escape")
												setCitySuggestions([]);
										}}
										className='h-10 sm:h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
										placeholder='Ej: Caracas'
									/>
									{citySuggestions.length > 0 ? (
										<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-md'>
											{citySuggestions.map((suggestion) => (
												<li
													key={`city-${suggestion.value}-${suggestion.label}`}
													className='border-b border-slate-100 last:border-b-0'
												>
													<button
														type='button'
														className='w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
														onMouseDown={(event) => {
															event.preventDefault();
															selectCitySuggestion(
																suggestion,
															);
														}}
													>
														{suggestion.label}
													</button>
												</li>
											))}
										</ul>
									) : null}
								</div>
								{citySuggestStatus === "loading" ? (
									<p className='mt-1 text-xs text-slate-500'>
										Buscando sugerencias...
									</p>
								) : null}
								{addressErrors.city ? (
									<p className='mt-1 text-xs text-red-500'>
										{addressErrors.city}
									</p>
								) : null}
							</div>

							<div>
								<label className='mb-1 block text-sm font-medium text-slate-700'>
									Calle
								</label>
								<input
									type='text'
									value={addressForm.street}
									onChange={(e) => {
										setAddressForm((prev) => ({
											...prev,
											street: e.target.value,
										}));
										setAddressErrors((prev) => ({
											...prev,
											street: undefined,
										}));
									}}
									className='h-10 sm:h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
									placeholder='Ej: Av. Principal, Calle 2'
								/>
								{addressErrors.street ? (
									<p className='mt-1 text-xs text-red-500'>
										{addressErrors.street}
									</p>
								) : null}
							</div>

							<div>
								<label className='mb-1 block text-sm font-medium text-slate-700'>
									Referencia
								</label>
								<input
									type='text'
									value={addressForm.reference}
									onChange={(e) => {
										setAddressForm((prev) => ({
											...prev,
											reference: e.target.value,
										}));
										setAddressErrors((prev) => ({
											...prev,
											reference: undefined,
										}));
									}}
									className='h-10 sm:h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
									placeholder='Ej: Frente a la panadería'
								/>
								{addressErrors.reference ? (
									<p className='mt-1 text-xs text-red-500'>
										{addressErrors.reference}
									</p>
								) : null}
							</div>

							<div className='flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2'>
								<button
									type='button'
									onClick={closeAddressModal}
									className='cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50'
								>
									Cancelar
								</button>
								<button
									type='button'
									onClick={() => void handleSaveAddress()}
									disabled={isSavingAddress}
									className='cursor-pointer rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60'
								>
									{isSavingAddress ? "Guardando..." : "Guardar"}
								</button>
							</div>
						</div>
					</div>
				</div>
			) : null}
			<AlertModal
				open={alertState.open}
				onClose={handleCloseAlert}
				title={alertState.title}
				message={alertState.message}
				variant={alertState.variant}
				confirmText={alertState.confirmText}
			/>
			<ConfirmModal
				open={confirmState.open}
				title={confirmState.title}
				message={confirmState.message}
				confirmLabel={confirmState.confirmLabel}
				cancelLabel={confirmState.cancelLabel}
				variant={confirmState.variant}
				onCancel={handleConfirmCancel}
				onConfirm={handleConfirmAccept}
			/>
		</div>
	);
};
